import { useContext, useEffect, useRef, useState } from "react";
import siteConfig from "../config/siteConfig";
import { PageContext } from "../context/PageContext";
import { PageRatingContext, PageRatingContextActionType, PageRatingState } from "../context/PageRatingContext";
import { PageRating, RawPageRating } from "../models/pageRating/pageRating";
import sanityClient, { ClientConfig } from "@sanity/client";

const usePageRating = () => {
    const { _id } = useContext(PageContext);
    const [{state, pageRating}, dispatch] = useContext(PageRatingContext);
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef<AbortController>();

    // Set the abortControllerRef in useEffect, so that the gatsby SSR build does not complain
    useEffect(
        () => {
            abortControllerRef.current = new AbortController();

            const dataFetch = async () => {
                if (state === PageRatingState.Uninitialized) {
                    dispatch({
                        type: PageRatingContextActionType.LoadingBegin
                    });

                    const client = sanityClient({
                        projectId: siteConfig.sanityProjectId,
                        dataset: siteConfig.sanityDataset,
                        token: siteConfig.sanityReadToken,
                        useCdn: false
                    } as ClientConfig);
    
                    let initialPageRating: PageRating;

                    try {
                        const document = await client.getDocument(_id);
                        const rawPageRating = document?.pageRating as RawPageRating;

                        if (rawPageRating?.active) {
                            const accumulatedRatings = rawPageRating.accumulatedRatings;
                            const numRatings = rawPageRating.numRatings;

                            initialPageRating = {
                                active: true,
                                accumulatedRatings,
                                numRatings,
                                averageRating: numRatings > 0 ? accumulatedRatings / numRatings : null,
                                userRating: pageRating.active ? pageRating.userRating : null
                            };
                        }
                        else {
                            initialPageRating = {
                                active: false
                            };
                        }
                    }
                    catch (e) {
                        console.error(e);
                        initialPageRating = {
                            active: false
                        };
                    }

                    dispatch({
                        type: PageRatingContextActionType.LoadingEnd,
                        newPageRating: initialPageRating
                    });
                }
            };

            dataFetch();

            return () => abortControllerRef.current?.abort();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const updatePageRating = async (rating: number) => {
        if (pageRating.active &&
            siteConfig.pageRatingHandlerEndpoint?.length) {
            let numRatings = pageRating.numRatings ?? 0;
            let ratingDiff = rating;
                
            const isNew = pageRating.userRating === null;
            if (isNew) {
                numRatings += 1;
            }
            else {
                ratingDiff -= pageRating.userRating!;
            }
            
            const accumulatedRatings = pageRating.accumulatedRatings + ratingDiff;
            
            const newPageRating: PageRating = {
                active: true,
                accumulatedRatings,
                numRatings,
                averageRating: numRatings > 0 ? accumulatedRatings / numRatings : null,
                userRating: rating
            };
            
            const storageKey = `pageRating-${_id}`;
            localStorage.setItem(storageKey, `${rating}`);

            dispatch({
                type: PageRatingContextActionType.Update,
                newPageRating: newPageRating
            });

            const data = {
                dataset: siteConfig.sanityDataset,
                pageId: _id,
                rating: ratingDiff,
                isNew
            };

            try {
                setLoading(true);

                const response = await fetch(
                    siteConfig.pageRatingHandlerEndpoint,
                    {
                        method: "POST",
                        signal: abortControllerRef.current?.signal,
                        body: JSON.stringify(data),
                        headers: {
                            "Content-type": "application/json"
                        }
                    }
                );
                
                setLoading(false);

                if (response.ok) {
                    return {
                        success: true
                    };
                }
                else {
                    console.log(response);

                    return {
                        success: false,
                        error: response
                    };
                }
            }
            catch (e) {
                if (e.name !== "AbortError") {
                    console.error(e);
                    setLoading(false);
                }
    
                return {
                    success: false,
                    error: e
                };
            }
        }
    };

    return [
        pageRating,
        updatePageRating,
        loading
    ] as const;
};

export default usePageRating;